// src/utils/orderItemGrouping.js
import { getCrossSellMaxQty } from './orderCalculations';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const getItemMergeKey = (item) =>
  item.bundleGroupKey
    ? `bundle:${item.bundleGroupKey}`
    : `${item.productId}-${item.variationId || 'simple'}`;

export const newBundleGroupKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getBundleComponentQty = (row) => {
  if (row.componentQuantity != null) return Number(row.componentQuantity);
  const bundleQty = Number(row.bundleQuantity) || 1;
  return Number(row.quantity) / bundleQty;
};

export const explodeBundle = (product, { idFactory = (i) => Date.now() + i, bundleQuantity = 1 } = {}) => {
  const groupKey = newBundleGroupKey();
  const components = product.bundleItems || [];
  const firstQty = Number(components[0]?.quantity) || 1;
  return components.map((c, i) => {
    const compQty = Number(c.quantity) || 0;
    const unitValue = Number(c.unitValue) || 0;
    const compDiscount = Number(c.lineDiscount) || 0;
    return {
      id: idFactory(i),
      productId: c.productId,
      variationId: c.variationId || null,
      displayName: c.name,
      sku: c.sku,
      quantity: compQty * bundleQuantity,
      componentQuantity: compQty,
      unitPrice: unitValue,
      originalUnitPrice: unitValue,
      manuallyPriced: false,
      componentLineDiscount: compDiscount,
      lineDiscount: round2(compDiscount * bundleQuantity),
      manuallyDiscounted: false,
      overrideDeliveryCharge: null,
      offerDeliveryOverride: null,
      offers: [],
      weight: i === 0 && product.weight != null ? Number(product.weight) / firstQty : null,
      bundleProductId: product.productId,
      bundleGroupKey: groupKey,
      bundleQuantity,
      bundleName: product.displayName,
      bundleSku: product.sku || null,
      isCrossSell: false,
      crossSellQuantity: null,
      crossSellById: null,
      baselineQuantity: null,
      originalQuantity: null,
    };
  });
};

export const rescaleBundleRows = (rows, groupKey, qty) =>
  rows.map(row => {
    if (row.bundleGroupKey !== groupKey) return row;
    const compQty = getBundleComponentQty(row);
    const compDiscount = row.componentLineDiscount != null
      ? Number(row.componentLineDiscount)
      : (Number(row.lineDiscount) || 0) / (Number(row.bundleQuantity) || 1);
    const quantity = compQty * qty;
    return {
      ...row,
      componentQuantity: compQty,
      componentLineDiscount: compDiscount,
      bundleQuantity: qty,
      quantity,
      lineDiscount: round2(compDiscount * qty),
      crossSellQuantity: row.crossSellQuantity != null ? Math.min(row.crossSellQuantity, quantity) : row.crossSellQuantity,
    };
  });

export const getBundleCrossSellMax = (rows, ctx) => {
  const first = rows[0];
  if (!first) return 0;
  const compQty = getBundleComponentQty(first) || 1;
  const toBundles = (v) => (v != null ? Number(v) / compQty : v);
  const proxy = {
    quantity: Number(first.bundleQuantity) || 0,
    baselineQuantity: toBundles(first.baselineQuantity),
    originalQuantity: toBundles(first.originalQuantity),
    crossSellById: first.crossSellById,
  };
  return Math.floor(getCrossSellMaxQty(proxy, ctx));
};

export const getBundleCrossSellCount = (rows) => {
  const first = rows[0];
  if (!first || !first.isCrossSell) return 0;
  return Math.round((Number(first.crossSellQuantity) || 0) / (getBundleComponentQty(first) || 1));
};

export const applyBundleCrossSell = (rows, groupKey, enabled, bundlesN) =>
  rows.map(row => {
    if (row.bundleGroupKey !== groupKey) return row;
    return {
      ...row,
      isCrossSell: !!enabled,
      crossSellQuantity: enabled ? bundlesN * getBundleComponentQty(row) : row.crossSellQuantity,
    };
  });

const rowTotal = (item) =>
  item.lineTotal != null
    ? Number(item.lineTotal)
    : (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) - (Number(item.lineDiscount) || 0);

export const groupOrderItems = (items = []) => {
  const result = [];
  const groups = new Map();
  items.forEach(item => {
    if (!item.bundleGroupKey) {
      result.push({ type: 'item', item });
      return;
    }
    let group = groups.get(item.bundleGroupKey);
    if (!group) {
      group = {
        type: 'bundle',
        groupKey: item.bundleGroupKey,
        bundleProductId: item.bundleProductId,
        bundleProduct: item.bundleProduct || null,
        bundleName: item.bundleProduct?.name || item.bundleName || null,
        bundleSku: item.bundleProduct?.sku || item.bundleSku || null,
        bundleQuantity: Number(item.bundleQuantity) || 1,
        items: [],
        total: 0,
      };
      groups.set(item.bundleGroupKey, group);
      result.push(group);
    }
    group.items.push(item);
    group.total = round2(group.total + rowTotal(item));
  });
  return result;
};
